"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BackButton } from "@/components/ui/BackButton";
import { useState, useEffect } from "react";
import { isValidEmail } from "@/lib/utils";
import { emailSignIn } from "@/lib/signInUtils";
import { handleError } from "@/lib/apiUtils";
import Link from "next/link";

type EmailAuthFormProps = {
  onBack?: () => void;
  callbackUrl?: string;
  title?: string;
  buttonText?: string;
  showTerms?: boolean;
  className?: string;
  inputClassName?: string;
  buttonClassName?: string;
};

const EmailAuthForm: React.FC<EmailAuthFormProps> = ({
  onBack,
  callbackUrl,
  title = "Create an account",
  buttonText = "Get Started",
  showTerms = true,
  className = "",
  inputClassName = "",
  buttonClassName = "",
}) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const isValid = isValidEmail(email);

  // Reset state when component mounts or callbackUrl changes
  useEffect(() => {
    setEmail("");
    setIsLoading(false);
  }, [callbackUrl]);

  const handleBackToOptions = () => {
    setEmail("");
    onBack?.();
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || isLoading) return;

    // Set loading immediately for better UX
    setIsLoading(true);

    // Small delay to ensure loading state is visible
    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      // Call emailSignIn - this will trigger the redirect to verification page
      emailSignIn(email, callbackUrl);

      // The user will be redirected to the verification page,
      // so we don't need to manage the loading state here
      // NextAuth will handle the redirect automatically
    } catch (error) {
      setIsLoading(false);
      // Show a user-friendly error message
      handleError(error, {
        reportError: true,
      });
    }
  };

  return (
    <div className={className}>
      {(onBack || showTerms) && (
        <div className="mb-6">
          {onBack && (
            <BackButton onClick={handleBackToOptions} className="self-start" />
          )}
          {title && (
            <h2 className="text-center text-3xl font-semibold text-white">
              {title}
            </h2>
          )}
        </div>
      )}

      <form
        onSubmit={handleEmailSubmit}
        className="flex w-full flex-col items-center justify-center gap-y-5 py-5"
      >
        <div className="w-full max-w-[240px] md:max-w-[320px]">
          <Input
            className={`text-regular flex h-[60px] w-full rounded-lg border border-white border-opacity-25 px-6 text-white backdrop-blur-[20px] backdrop-filter focus:border-opacity-50 active:border-opacity-50 ${inputClassName}`}
            name="emailAddress"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value.toLowerCase())}
            containerClassName="w-full flex-1"
            disabled={isLoading}
            required
          />
        </div>
        <Button
          type="submit"
          className={`w-full max-w-[240px] bg-primary-gold md:max-w-[320px] ${buttonClassName}`}
          disabled={!isValid || isLoading}
          loading={isLoading}
        >
          {buttonText}
        </Button>
      </form>

      {showTerms && (
        <div className="px-6 pt-2 text-center text-xs text-neutral-300">
          By creating an account you are accepting Gondola&apos;s{" "}
          <Link
            href="/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Privacy Policy
          </Link>
        </div>
      )}
    </div>
  );
};

export default EmailAuthForm;
